import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Lock, LogOut, MapPin, Check, X, Eye, Clock, 
  Building2, Users, AlertTriangle, Wifi, Car, Coffee,
  ChevronDown, ChevronUp, Search, RefreshCw, Shield, Trash2,
  FileText, PoundSterling, Send, Printer, Plus, Settings, Save, Loader2, BarChart3,
  Crown, Mail, Phone, ArrowUp, ArrowDown, Edit, Globe, Plug, Volume2, UtensilsCrossed, GlassWater,
  Bell, BellOff, BellRing, Bug, Activity, CheckCircle, XCircle, Zap, Play, Square, History, Bot, Timer,
  Palette, Image, Calendar, Trophy, AlertCircle, ExternalLink, Smartphone, Monitor, Gift, Ticket, Copy, Download, Flag
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { AdminAnalytics } from "../components/AnalyticsDashboard";
import { getVenueImage } from "../utils/defaultImages";
import AdminReviewsTab from "../components/admin/AdminReviewsTab";
import AdminGeographyTab from "../components/admin/AdminGeographyTab";
import AdminOwnersTab from "../components/admin/AdminOwnersTab";
import AdminContributorsTab from "../components/admin/AdminContributorsTab";
import AdminLeaderboardTab from "../components/admin/AdminLeaderboardTab";
import AdminInvoicesTab from "../components/admin/AdminInvoicesTab";
import AdminVouchersTab from "../components/admin/AdminVouchersTab";
import AdminSettingsTab from "../components/admin/AdminSettingsTab";
import AdminSubmissionsTab from "../components/admin/AdminSubmissionsTab";
import AdminErrorReportsTab from "../components/admin/AdminErrorReportsTab";
import AdminEventsTab from "../components/admin/AdminEventsTab";
import AdminInquiriesTab from "../components/admin/AdminInquiriesTab";
import AdminSystemTab from "../components/admin/AdminSystemTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin authentication - password verified server-side
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "change_me_in_production";

const wifiLabels = {
  very_fast: "Very Fast", fast: "Fast", good: "Good", poor: "Poor", none: "None",
  excellent: "Excellent", fair: "Fair"
};

const noiseLabels = { quiet: "Quiet", moderate: "Moderate", lively: "Lively" };

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  
  // Check if already authenticated (shared admin session)
  useEffect(() => {
    const adminAuth = sessionStorage.getItem("strider_admin_auth");
    if (adminAuth === "true") {
      setIsAuthenticated(true);
    }
  }, []);
  
  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  const [submissions, setSubmissions] = useState([]);
  const [venues, setVenues] = useState([]);
  const [owners, setOwners] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [errorReports, setErrorReports] = useState([]);
  const [systemErrors, setSystemErrors] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [errorStats, setErrorStats] = useState(null);
  
  // AI Testing state - managed by AdminSystemTab
  const [aiTestingStatus, setAiTestingStatus] = useState(null);
  const [aiTestHistory, setAiTestHistory] = useState([]);
  const [aiAutoFixes, setAiAutoFixes] = useState([]);
  const [latestTestRun, setLatestTestRun] = useState(null);
  
  // Vouchers state - form state now handled by AdminVouchersTab component
  const [vouchers, setVouchers] = useState([]);
  
  // Contributors state
  const [contributors, setContributors] = useState([]);
  const [contributorStats, setContributorStats] = useState({ total: 0, banned: 0 });
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewVenues, setReviewVenues] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [pendingSubmissionReviews, setPendingSubmissionReviews] = useState([]);
  const [reviewFilters, setReviewFilters] = useState({
    status: "all",
    sort_by: "review_count",
    sort_order: "desc"
  });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [expandedVenues, setExpandedVenues] = useState({});
  
  // Geography state (Countries & Cities)
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [countryForm, setCountryForm] = useState({ code: "", name: "", slug: "" });
  const [cityForm, setCityForm] = useState({
    slug: "", name: "", country_id: "", lat: "", lng: "",
    description: "", highlights: [], variations: []
  });
  const [newHighlight, setNewHighlight] = useState("");
  const [newVariation, setNewVariation] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("analytics");
  const [expandedItem, setExpandedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [processing, setProcessing] = useState(null);
  
  // Sort state for admin lists
  const [sortConfig, setSortConfig] = useState({
    owners: { field: "name", direction: "asc" },
    venues: { field: "name", direction: "asc" },
    contributors: { field: "total_xp", direction: "desc" },
    submissions: { field: "created_at", direction: "desc" },
    errorReports: { field: "submitted_at", direction: "desc" },
    reviewVenues: { field: "review_count", direction: "desc" }
  });
  
  // Notification state
  const [lastPendingCount, setLastPendingCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState("default");
  
  // Venue edit modal
  const [showVenueEditModal, setShowVenueEditModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [venueEditForm, setVenueEditForm] = useState({});
  
  // Add new item modals
  const [showAddVenueModal, setShowAddVenueModal] = useState(false);
  const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
  const [showAddContributorModal, setShowAddContributorModal] = useState(false);
  const [deleteContributorModal, setDeleteContributorModal] = useState({ show: false, contributor: null });
  
  // New item forms
  const [newVenueForm, setNewVenueForm] = useState({
    name: "", venue_type: "cafe", description: "",
    address: "", city: "", postcode: "", neighborhood: "",
    wifi_quality: "good", noise_level: "moderate", power_outlets: 5,
    has_meeting_rooms: false, has_parking: false, has_food: true, has_drinks: true,
    coffee_avg: 3, food_avg: 8, opening_hours: "", is_premium: false
  });
  const [newOwnerForm, setNewOwnerForm] = useState({
    email: "", business_name: "", contact_name: "", phone: ""
  });
  const [newContributorForm, setNewContributorForm] = useState({
    email: "", name: "", username: "", xp: 0, level: "Newcomer", avatar: ""
  });
  
  // Expanded owner state for dropdown
  const [expandedOwners, setExpandedOwners] = useState({});
  
  // E2E Test state - managed by AdminSystemTab
  const [e2eTestReports, setE2eTestReports] = useState([]);
  const [selectedE2eReport, setSelectedE2eReport] = useState(null);
  
  // Community Events state
  const [communityEvents, setCommunityEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "", description: "", location: "", city: "",
    date: "", time: "19:00", recurring: "", max_attendees: null, image_url: "", is_active: true
  });

  // Sort helper function - define first before useMemo hooks that use it
  const sortData = useCallback((data, config) => {
    if (!config || !config.field) return data;
    return [...data].sort((a, b) => {
      let aVal = a[config.field];
      let bVal = b[config.field];
      
      // Handle nested fields
      if (config.field.includes('.')) {
        const parts = config.field.split('.');
        aVal = parts.reduce((obj, key) => obj?.[key], a);
        bVal = parts.reduce((obj, key) => obj?.[key], b);
      }
      
      // Handle null/undefined
      if (aVal == null) return config.direction === 'asc' ? 1 : -1;
      if (bVal == null) return config.direction === 'asc' ? -1 : 1;
      
      // Handle strings
      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      
      // Compare
      if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, []);

  // Handle sort column click
  const handleSort = useCallback((listType, field) => {
    setSortConfig(prev => ({
      ...prev,
      [listType]: {
        field,
        direction: prev[listType]?.field === field && prev[listType]?.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  }, []);

  // Memoized filtered lists for performance - must be after sortData definition
  const filteredSubmissions = useMemo(() => {
    const filtered = submissions.filter(s => 
      !searchQuery ||
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.city?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filtered, sortConfig.submissions);
  }, [submissions, searchQuery, sortConfig.submissions, sortData]);

  const filteredVenues = useMemo(() => {
    const filtered = venues.filter(v => 
      !searchQuery ||
      v.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.location?.city?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filtered, sortConfig.venues);
  }, [venues, searchQuery, sortConfig.venues, sortData]);

  const filteredOwners = useMemo(() => {
    const filtered = owners.filter(o => 
      !searchQuery ||
      o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filtered, sortConfig.owners);
  }, [owners, searchQuery, sortConfig.owners, sortData]);

  const filteredContributors = useMemo(() => {
    const filtered = contributors.filter(c => 
      !searchQuery ||
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filtered, sortConfig.contributors);
  }, [contributors, searchQuery, sortConfig.contributors, sortData]);

  const filteredReviewVenues = useMemo(() => {
    const filtered = reviewVenues.filter(venue => 
      !searchQuery || 
      venue.venue_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      venue.venue_city?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filtered, sortConfig.reviewVenues);
  }, [reviewVenues, searchQuery, sortConfig.reviewVenues, sortData]);
  
  // Filtered error reports with sorting
  const filteredErrorReports = useMemo(() => {
    const filtered = errorReports.filter(r => 
      !searchQuery ||
      r.venue_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.message?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortData(filtered, sortConfig.errorReports);
  }, [errorReports, searchQuery, sortConfig.errorReports, sortData]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  // PWA Install Handler
  useEffect(() => {
    // Check if running as standalone PWA
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                             window.navigator.standalone === true;
    setIsStandalone(isStandaloneMode);
    
    // Add admin manifest link dynamically
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.href = '/manifest-admin.json';
    }
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstalled(false);
    };
    
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      toast.success("Admin Portal installed! You can now open it from your home screen.");
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Install PWA handler
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info("To install: Open in Safari/Chrome, tap Share/Menu, then 'Add to Home Screen'");
      return;
    }
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success("Installing Admin Portal...");
    }
    setDeferredPrompt(null);
  };

  // Update app badge when pending count changes
  const updateAppBadge = (count) => {
    try {
      if ("setAppBadge" in navigator) {
        if (count > 0) {
          navigator.setAppBadge(count).catch(() => {});
        } else {
          navigator.clearAppBadge().catch(() => {});
        }
      }
      // Also update document title
      document.title = count > 0 ? `(${count}) Strider Admin` : "Strider Admin";
    } catch (e) {
      console.error("Badge update error:", e);
    }
  };

  // Send desktop notification
  const sendNotification = (title, body, tag = "wafw-admin") => {
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, {
          body,
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag,
          requireInteraction: false
        });
      }
    } catch (e) {
      console.error("Notification error:", e);
    }
  };

  // Auto-refresh interval (every 60 seconds) - separate from main data fetch
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Set up interval for background refresh
    const interval = setInterval(async () => {
      try {
        const [subsRes, reportsRes] = await Promise.all([
          axios.get(`${API}/locations/pending`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/error-reports`).catch(() => ({ data: [] }))
        ]);
        
        const pendingSubmissions = subsRes.data.length;
        const pendingReports = reportsRes.data.filter(r => r.status === "pending").length;
        const totalPending = pendingSubmissions + pendingReports;
        
        // Check if there are new items (only notify if count increased)
        setLastPendingCount(prev => {
          if (totalPending > prev && prev > 0) {
            const newItems = totalPending - prev;
            sendNotification(
              "New items pending review",
              `${newItems} new item${newItems > 1 ? 's' : ''} require${newItems === 1 ? 's' : ''} your attention`,
              "wafw-pending"
            );
          }
          return totalPending;
        });
        
        updateAppBadge(totalPending);
        
        // Update the data silently
        setSubmissions(subsRes.data);
        setErrorReports(reportsRes.data);
        
      } catch (error) {
        console.error("Auto-refresh failed:", error);
      }
    }, 60000); // 60 seconds
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  
  // Fetch reviews when Reviews tab is activated
  useEffect(() => {
    if (isAuthenticated && activeTab === "reviews" && reviewVenues.length === 0) {
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAuthenticated]);
  
  // Fetch E2E reports when System tab is activated
  useEffect(() => {
    if (isAuthenticated && activeTab === "system") {
      fetchE2eReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAuthenticated]);
  
  // Pricing management - state moved to AdminSettingsTab
  const [pricing, setPricing] = useState({ premium_annual: 149, premium_monthly: 15 });
  
  // Rejection modal
  const [rejectModal, setRejectModal] = useState({ open: false, submissionId: null, name: '' });
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Admin token stored in memory (React state) - not in sessionStorage for security
  const [adminToken, setAdminToken] = useState("");

  useEffect(() => {
    if (isAuthenticated) fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      // Server-side password validation
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        // Store token in memory for this session
        if (response.data.token) {
          setAdminToken(response.data.token);
        }
        setIsAuthenticated(true);
        // Store session for shared admin pages (like Moderation)
        sessionStorage.setItem("strider_admin_auth", "true");
        toast.success("Logged in successfully");
        
        // Mark this session as admin to exclude from analytics
        try {
          const sessionId = sessionStorage.getItem("analytics_session_id");
          if (sessionId) {
            await axios.post(`${API}/admin/mark-session`, { session_id: sessionId });
          }
        } catch {
          // Non-critical - don't block login
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("Invalid password");
    }
  };

  const handleLogout = () => {
    setAdminToken("");
    setIsAuthenticated(false);
    sessionStorage.removeItem("strider_admin_auth");
    navigate("/");
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel with individual error handling
      const results = await Promise.allSettled([
        axios.get(`${API}/locations/pending`),
        axios.get(`${API}/venues?limit=100`),
        axios.get(`${API}/owners`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/corporate/inquiries`),
        axios.get(`${API}/pricing`),
        axios.get(`${API}/admin/error-reports`),
        axios.get(`${API}/system/errors/stats`),
        axios.get(`${API}/system/errors?limit=50&unresolved_only=true`),
        axios.get(`${API}/system/health`),
        // AI Testing data
        axios.get(`${API}/ai-testing/status`),
        axios.get(`${API}/ai-testing/history?limit=10`),
        axios.get(`${API}/ai-testing/fixes?limit=20`),
        axios.get(`${API}/ai-testing/latest`),
        // Contributors data
        axios.get(`${API}/contributors/all`),
        axios.get(`${API}/moderation/dashboard`)
      ]);
      
      // Extract data with fallbacks for failed requests
      const [subsRes, venuesRes, ownersRes, invoicesRes, inquiriesRes, pricingRes, errorReportsRes, errorStatsRes, systemErrorsRes, healthRes, aiStatusRes, aiHistoryRes, aiFixesRes, aiLatestRes, contributorsRes, moderationRes] = results;
      
      setSubmissions(subsRes.status === 'fulfilled' ? subsRes.value.data : []);
      setVenues(venuesRes.status === 'fulfilled' ? venuesRes.value.data : []);
      setOwners(ownersRes.status === 'fulfilled' ? ownersRes.value.data : []);
      setInvoices(invoicesRes.status === 'fulfilled' ? invoicesRes.value.data : []);
      setInquiries(inquiriesRes.status === 'fulfilled' ? inquiriesRes.value.data : []);
      
      const pricingData = pricingRes.status === 'fulfilled' ? pricingRes.value.data : { premium_annual: 149, premium_monthly: 15 };
      setPricing(pricingData);
      
      const reports = errorReportsRes.status === 'fulfilled' ? errorReportsRes.value.data : [];
      setErrorReports(reports);
      
      // System error monitoring data
      setErrorStats(errorStatsRes.status === 'fulfilled' ? errorStatsRes.value.data : null);
      setSystemErrors(systemErrorsRes.status === 'fulfilled' ? systemErrorsRes.value.data : []);
      setSystemHealth(healthRes.status === 'fulfilled' ? healthRes.value.data : null);
      
      // AI Testing data
      setAiTestingStatus(aiStatusRes.status === 'fulfilled' ? aiStatusRes.value.data : null);
      setAiTestHistory(aiHistoryRes.status === 'fulfilled' ? aiHistoryRes.value.data : []);
      setAiAutoFixes(aiFixesRes.status === 'fulfilled' ? aiFixesRes.value.data : []);
      setLatestTestRun(aiLatestRes.status === 'fulfilled' ? aiLatestRes.value.data : null);
      
      // Contributors data
      setContributors(contributorsRes.status === 'fulfilled' ? contributorsRes.value.data : []);
      const modData = moderationRes.status === 'fulfilled' ? moderationRes.value.data : { stats: {} };
      setContributorStats({
        total: modData.stats?.total_contributors || 0,
        banned: modData.stats?.banned_contributors || 0
      });
      
      // Fetch geography data
      fetchGeographyData();
      
      // Fetch vouchers
      fetchVouchers();
      
      // Fetch community events
      fetchCommunityEvents();
      
      // Fetch pending submission reviews
      fetchPendingSubmissionReviews();
      
      // Update badge with pending count
      const pendingCount = (subsRes.status === 'fulfilled' ? subsRes.value.data.length : 0);
      const pendingReports = reports.filter(r => r.status === "pending").length;
      const totalPending = pendingCount + pendingReports;
      setLastPendingCount(totalPending);
      updateAppBadge(totalPending);
      
      // Log any failed requests for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['pending', 'venues', 'owners', 'invoices', 'inquiries', 'pricing', 'error-reports', 'error-stats', 'system-errors', 'health', 'ai-status', 'ai-history', 'ai-fixes', 'ai-latest'];
          console.warn(`Failed to fetch ${endpoints[index]}:`, result.reason?.message);
        }
      });
      
    } catch (error) {
      console.error("fetchAllData error:", error);
      toast.error("Some data failed to load. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  };

  // Voucher Management Functions
  const fetchVouchers = async () => {
    try {
      const res = await axios.get(`${API}/admin/vouchers`);
      setVouchers(res.data.vouchers || []);
    } catch (error) {
      console.error("Failed to fetch vouchers:", error);
    }
  };

  // Community Events Management Functions
  const fetchCommunityEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/community-events`);
      setCommunityEvents(res.data.events || []);
    } catch (error) {
      console.error("Failed to fetch community events:", error);
    }
  };

  // Fetch pending reviews from venue submissions
  const fetchPendingSubmissionReviews = async () => {
    try {
      const res = await axios.get(`${API}/moderation/pending-reviews`);
      const reviews = res.data || [];
      setPendingSubmissionReviews(reviews);
      
      // Show notification if there are pending reviews
      if (reviews.length > 0) {
        toast.info(`${reviews.length} review${reviews.length > 1 ? 's' : ''} awaiting verification`, {
          duration: 5000,
          icon: '📝',
        });
      }
    } catch (error) {
      console.error("Failed to fetch pending submission reviews:", error);
    }
  };

  // Approve a submission review
  const approveSubmissionReview = async (reviewId) => {
    try {
      await axios.post(`${API}/moderation/reviews/${reviewId}/approve-submission-review`);
      toast.success("Review approved!");
      fetchPendingSubmissionReviews();
      fetchReviews(); // Refresh main reviews list
    } catch (error) {
      console.error("Failed to approve review:", error);
      toast.error("Failed to approve review");
    }
  };

  // Reject a submission review
  const rejectSubmissionReview = async (reviewId) => {
    try {
      await axios.put(`${API}/moderation/reviews/${reviewId}/status`, { status: "removed" });
      toast.success("Review rejected");
      fetchPendingSubmissionReviews();
    } catch (error) {
      console.error("Failed to reject review:", error);
      toast.error("Failed to reject review");
    }
  };

  const saveEvent = async () => {
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/community-events/${editingEvent.id}`, eventForm);
        toast.success("Event updated!");
      } else {
        await axios.post(`${API}/admin/community-events`, eventForm);
        toast.success("Event created!");
      }
      setShowEventModal(false);
      setEditingEvent(null);
      setEventForm({ title: "", description: "", location: "", city: "", date: "", time: "19:00", recurring: "", max_attendees: null, image_url: "", is_active: true });
      fetchCommunityEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save event");
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/community-events/${eventId}`);
      toast.success("Event deleted");
      fetchCommunityEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  // Geography Management Functions
  const fetchGeographyData = async () => {
    try {
      const [countriesRes, citiesRes] = await Promise.allSettled([
        axios.get(`${API}/admin/countries`),
        axios.get(`${API}/admin/cities`)
      ]);
      setCountries(countriesRes.status === 'fulfilled' ? countriesRes.value.data : []);
      setCities(citiesRes.status === 'fulfilled' ? citiesRes.value.data : []);
    } catch (error) {
      console.error("Failed to fetch geography data:", error);
    }
  };

  // Reviews Management Functions
  const fetchReviews = async (filters = reviewFilters) => {
    setReviewsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      params.append("sort_by", filters.sort_by);
      params.append("sort_order", filters.sort_order);
      
      const response = await axios.get(`${API}/admin/reviews/by-venue?${params.toString()}`);
      setReviewVenues(response.data.venues || []);
      setReviewsTotal(response.data.total_reviews || 0);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      toast.error("Failed to load reviews");
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewStatusChange = async (reviewId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/reviews/${reviewId}/status?status=${newStatus}`);
      toast.success(`Review ${newStatus}`);
      fetchReviews();
    } catch (error) {
      toast.error("Failed to update review status");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to permanently delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      toast.success("Review deleted");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const seedGeographyData = async () => {
    try {
      const response = await axios.post(`${API}/admin/geography/seed`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchGeographyData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to seed data");
    }
  };

  const handleSaveCountry = async () => {
    try {
      if (editingCountry) {
        await axios.put(`${API}/admin/countries/${editingCountry.id}`, countryForm);
        toast.success("Country updated");
      } else {
        await axios.post(`${API}/admin/countries`, countryForm);
        toast.success("Country created");
      }
      setShowCountryModal(false);
      setEditingCountry(null);
      setCountryForm({ code: "", name: "", slug: "" });
      fetchGeographyData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to save country");
    }
  };

  const handleDeleteCountry = async (countryId) => {
    if (!window.confirm("Delete this country? All cities must be removed first.")) return;
    try {
      await axios.delete(`${API}/admin/countries/${countryId}`);
      toast.success("Country deleted");
      fetchGeographyData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to delete country");
    }
  };

  const handleSaveCity = async () => {
    try {
      const cityData = {
        ...cityForm,
        lat: parseFloat(cityForm.lat),
        lng: parseFloat(cityForm.lng)
      };
      
      if (editingCity) {
        await axios.put(`${API}/admin/cities/${editingCity.id}`, cityData);
        toast.success("City updated");
      } else {
        await axios.post(`${API}/admin/cities`, cityData);
        toast.success("City created");
      }
      setShowCityModal(false);
      setEditingCity(null);
      setCityForm({ slug: "", name: "", country_id: "", lat: "", lng: "", description: "", highlights: [], variations: [] });
      fetchGeographyData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to save city");
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (!window.confirm("Delete this city?")) return;
    try {
      await axios.delete(`${API}/admin/cities/${cityId}`);
      toast.success("City deleted");
      fetchGeographyData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to delete city");
    }
  };

  const openEditCountry = (country) => {
    setEditingCountry(country);
    setCountryForm({ code: country.code, name: country.name, slug: country.slug });
    setShowCountryModal(true);
  };

  const openEditCity = (city) => {
    setEditingCity(city);
    setCityForm({
      slug: city.slug,
      name: city.name,
      country_id: city.country_id,
      lat: city.lat?.toString() || "",
      lng: city.lng?.toString() || "",
      description: city.description || "",
      highlights: city.highlights || [],
      variations: city.variations || []
    });
    setShowCityModal(true);
  };

  const openNewCity = (countryId = null) => {
    setEditingCity(null);
    setCityForm({
      slug: "",
      name: "",
      country_id: countryId || (countries[0]?.id || ""),
      lat: "",
      lng: "",
      description: "",
      highlights: [],
      variations: []
    });
    setShowCityModal(true);
  };

  const handleApprove = async (submission) => {
    setProcessing(submission.id);
    try {
      await axios.post(`${API}/locations/${submission.id}/approve?moderator_email=admin`);
      toast.success(`"${submission.name}" approved and published!`);
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (submission, reason = "") => {
    setProcessing(submission.id);
    try {
      await axios.post(`${API}/locations/${submission.id}/reject`, { reason });
      toast.success(`"${submission.name}" rejected`);
      setRejectModal({ open: false, submissionId: null, name: '' });
      setRejectionReason('');
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to reject");
    } finally {
      setProcessing(null);
    }
  };
  
  const openRejectModal = (submission) => {
    setRejectModal({ open: true, submissionId: submission.id, name: submission.name });
    setRejectionReason('');
  };

  const handleDeleteVenue = async (venueId, venueName) => {
    const confirmed = window.confirm(`DELETE "${venueName}"?\n\nThis cannot be undone.`);
    if (!confirmed) {
      return;
    }
    
    setProcessing(venueId);
    
    try {
      await axios.delete(`${API}/venues/${venueId}`);
      toast.success(`"${venueName}" deleted successfully`);
      // Refresh data
      await fetchAllData();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to delete venue");
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteOwner = async (ownerId, ownerName) => {
    const confirmed = window.confirm(`DELETE OWNER "${ownerName}"?\n\nThis will:\n- Remove their account\n- Unclaim all their venues\n\nThis cannot be undone.`);
    if (!confirmed) return;
    
    setProcessing(ownerId);
    try {
      await axios.delete(`${API}/owners/${ownerId}`);
      toast.success(`Owner "${ownerName}" deleted and venues unclaimed`);
      await fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to delete owner");
    } finally {
      setProcessing(null);
    }
  };

  const handleToggleSubscription = async (ownerId, ownerName, currentTier) => {
    const newTier = currentTier === "premium" ? "free" : "premium";
    const action = newTier === "premium" ? "UPGRADE" : "DOWNGRADE";
    
    const confirmed = window.confirm(`${action} "${ownerName}" to ${newTier.toUpperCase()}?\n\nThis will also update all their venues' premium status.`);
    if (!confirmed) return;
    
    setProcessing(ownerId);
    try {
      await axios.patch(`${API}/owners/${ownerId}/subscription?tier=${newTier}`);
      toast.success(`Owner ${action.toLowerCase()}d to ${newTier}`);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action.toLowerCase()} owner`);
    } finally {
      setProcessing(null);
    }
  };

  // Invoice status and delete handlers - still used by AdminInvoicesTab
  const handleUpdateInvoiceStatus = async (invoiceId, status) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}/status?status=${status}`);
      toast.success(`Invoice marked as ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const handleResolveErrorReport = async (reportId, status) => {
    setProcessing(reportId);
    try {
      await axios.patch(`${API}/admin/error-reports/${reportId}?status=${status}`);
      toast.success(`Report marked as ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update report");
    } finally {
      setProcessing(null);
    }
  };

  const handleReGeocode = async (venueId, venueName) => {
    setProcessing(venueId);
    try {
      const response = await axios.post(`${API}/admin/venues/${venueId}/re-geocode`);
      if (response.data.success) {
        toast.success(`"${venueName}" re-geocoded: ${response.data.new_coords.lat.toFixed(4)}, ${response.data.new_coords.lng.toFixed(4)}`);
        fetchAllData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to re-geocode venue");
    } finally {
      setProcessing(null);
    }
  };

  // Run WiFi speed test for a venue - using accurate multi-round testing
  const handleWiFiTest = async (venueId, venueName) => {
    setProcessing(venueId);
    toast.info(`Running WiFi speed test for "${venueName}"... (10-15 seconds)`);
    
    try {
      // Test files of various sizes for accurate measurement
      const testFiles = [
        { url: 'https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js', size: 87000 },
        { url: 'https://cdn.jsdelivr.net/npm/lodash@4.17.21/lodash.min.js', size: 72000 },
        { url: 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js', size: 80000 },
        { url: 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js', size: 200000 },
        { url: 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.min.js', size: 650000 },
      ];
      
      let totalBytes = 0;
      let totalTimeMs = 0;
      const samples = [];
      
      // Run 3 rounds for accuracy
      for (let round = 0; round < 3; round++) {
        for (const file of testFiles) {
          try {
            const url = file.url + '?t=' + Date.now() + '_' + Math.random();
            const startTime = performance.now();
            const response = await fetch(url, { cache: 'no-store', mode: 'cors' });
            const data = await response.arrayBuffer();
            const endTime = performance.now();
            
            const durationMs = endTime - startTime;
            const bytes = data.byteLength;
            const speedMbps = (bytes * 8 / (durationMs / 1000)) / 1000000;
            
            totalBytes += bytes;
            totalTimeMs += durationMs;
            samples.push(speedMbps);
          } catch (err) {
            // Expected: Some speed test requests may fail
          }
        }
      }
      
      if (samples.length === 0) {
        toast.error("Speed test failed - couldn't reach test servers");
        return;
      }
      
      // Calculate overall speed (total bytes / total time - most accurate)
      const downloadMbps = Math.round((totalBytes * 8 / (totalTimeMs / 1000)) / 1000000 * 10) / 10;
      
      // Test latency with more samples
      const latencies = [];
      for (let i = 0; i < 8; i++) {
        try {
          const start = performance.now();
          await fetch('https://cdn.jsdelivr.net/npm/lodash@4.17.21/package.json?ping=' + Date.now() + '_' + i, { cache: 'no-store' });
          latencies.push(performance.now() - start);
        } catch (e) {
          // Expected: Some latency tests may fail
        }
      }
      
      // Remove outliers from latency
      let latencyMs = 30;
      if (latencies.length > 0) {
        const sorted = latencies.sort((a, b) => a - b);
        const trimmed = sorted.length > 2 ? sorted.slice(1, -1) : sorted;
        latencyMs = Math.round(trimmed.reduce((a, b) => a + b, 0) / trimmed.length);
      }
      
      // Estimate upload (typically 20-40% of download depending on connection)
      const uploadMbps = Math.round(downloadMbps * 0.3 * 10) / 10;
      
      // Get time of day
      const hour = new Date().getHours();
      let timeOfDay = 'night';
      if (hour >= 5 && hour < 12) timeOfDay = 'morning';
      else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
      else if (hour >= 17 && hour < 21) timeOfDay = 'evening';
      
      // Submit to backend
      const response = await axios.post(`${API}/venues/${venueId}/wifi-speed`, {
        download_mbps: downloadMbps,
        upload_mbps: uploadMbps,
        latency_ms: latencyMs,
        device_type: 'desktop',
        time_of_day: timeOfDay
      });
      
      if (response.data.success) {
        toast.success(`WiFi speed updated for "${venueName}": ${response.data.current_avg_mbps} Mbps (${response.data.speed_tier}) - ${samples.length} samples, ${(totalBytes/1024/1024).toFixed(1)}MB tested`);
        fetchAllData();
      } else {
        toast.error("Failed to save WiFi speed data");
      }
    } catch (error) {
      console.error('WiFi test error:', error);
      toast.error("WiFi speed test failed");
    } finally {
      setProcessing(null);
    }
  };

  // Add new venue
  const handleAddVenue = async () => {
    if (!newVenueForm.name.trim() || !newVenueForm.city.trim()) {
      toast.error("Please enter venue name and city");
      return;
    }
    setProcessing("add-venue");
    try {
      // Geocode the address
      let lat = 51.5074, lng = -0.1278; // Default London
      if (newVenueForm.address || newVenueForm.city) {
        try {
          const geoQuery = `${newVenueForm.address}, ${newVenueForm.city}, ${newVenueForm.postcode}`.trim();
          const geoResponse = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(geoQuery)}&limit=1`);
          if (geoResponse.data?.[0]) {
            lat = parseFloat(geoResponse.data[0].lat);
            lng = parseFloat(geoResponse.data[0].lon);
          }
        } catch (e) { /* Use defaults */ }
      }
      
      const venueData = {
        name: newVenueForm.name,
        venue_type: newVenueForm.venue_type,
        description: newVenueForm.description || `A ${newVenueForm.venue_type} in ${newVenueForm.city}`,
        location: {
          address: newVenueForm.address,
          city: newVenueForm.city,
          postcode: newVenueForm.postcode,
          lat, lng
        },
        amenities: {
          wifi_quality: newVenueForm.wifi_quality,
          noise_level: newVenueForm.noise_level,
          power_outlets: parseInt(newVenueForm.power_outlets) || 5,
          has_meeting_rooms: newVenueForm.has_meeting_rooms,
          has_parking: newVenueForm.has_parking,
          has_food: newVenueForm.has_food,
          has_drinks: newVenueForm.has_drinks
        },
        price_range: {
          coffee_avg: parseInt(newVenueForm.coffee_avg) || 3,
          food_avg: parseInt(newVenueForm.food_avg) || 8
        },
        neighborhood: newVenueForm.neighborhood,
        opening_hours: newVenueForm.opening_hours,
        is_premium: newVenueForm.is_premium,
        rating: 0,
        review_count: 0
      };
      
      await axios.post(`${API}/venues`, venueData);
      toast.success(`Venue "${newVenueForm.name}" created successfully`);
      setShowAddVenueModal(false);
      setNewVenueForm({
        name: "", venue_type: "cafe", description: "",
        address: "", city: "", postcode: "", neighborhood: "",
        wifi_quality: "good", noise_level: "moderate", power_outlets: 5,
        has_meeting_rooms: false, has_parking: false, has_food: true, has_drinks: true,
        coffee_avg: 3, food_avg: 8, opening_hours: "", is_premium: false
      });
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to create venue");
    } finally {
      setProcessing(null);
    }
  };

  // Add new owner
  const handleAddOwner = async () => {
    if (!newOwnerForm.email.trim()) {
      toast.error("Please enter owner email");
      return;
    }
    if (!newOwnerForm.contact_name.trim()) {
      toast.error("Please enter contact name");
      return;
    }
    setProcessing("add-owner");
    try {
      await axios.post(`${API}/owners`, {
        email: newOwnerForm.email,
        name: newOwnerForm.contact_name,
        company_name: newOwnerForm.business_name || "",
        phone: newOwnerForm.phone || ""
      });
      toast.success(`Owner "${newOwnerForm.email}" created successfully`);
      setShowAddOwnerModal(false);
      setNewOwnerForm({ email: "", business_name: "", contact_name: "", phone: "" });
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to create owner");
    } finally {
      setProcessing(null);
    }
  };

  // Add new contributor
  const handleAddContributor = async () => {
    if (!newContributorForm.email.trim()) {
      toast.error("Please enter contributor email");
      return;
    }
    if (!newContributorForm.username.trim()) {
      toast.error("Please enter a username for the leaderboard");
      return;
    }
    setProcessing("add-contributor");
    try {
      await axios.post(`${API}/contributors`, {
        email: newContributorForm.email,
        name: newContributorForm.name || newContributorForm.username,
        username: newContributorForm.username,
        xp: parseInt(newContributorForm.xp) || 0,
        level: newContributorForm.level || "Newcomer",
        avatar: newContributorForm.avatar || null,
        badges: [],
        stats: { reviews: 0, helpful_votes_received: 0 }
      });
      toast.success(`Contributor "${newContributorForm.username}" created successfully`);
      setShowAddContributorModal(false);
      setNewContributorForm({ email: "", name: "", username: "", xp: 0, level: "Newcomer", avatar: "" });
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to create contributor");
    } finally {
      setProcessing(null);
    }
  };

  // Delete contributor handler
  const handleDeleteContributor = async () => {
    if (!deleteContributorModal.contributor) return;
    
    setProcessing("delete-contributor");
    try {
      await axios.delete(`${API}/admin/contributors/${deleteContributorModal.contributor.id}`, {
        params: { admin_password: ADMIN_PASSWORD }
      });
      toast.success(`Contributor "${deleteContributorModal.contributor.name}" deleted successfully`);
      setDeleteContributorModal({ show: false, contributor: null });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contributor");
    } finally {
      setProcessing(null);
    }
  };

  const fetchE2eReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/e2e-tests/reports?limit=10`);
      setE2eTestReports(response.data || []);
    } catch (error) {
      console.error("Failed to fetch E2E reports:", error);
    }
  };

  // Venue Edit Functions
  const openVenueEdit = async (venue) => {
    try {
      const response = await axios.get(`${API}/admin/venues/${venue.id}`);
      const venueData = response.data;
      
      // Find the owner if venue has owner_id
      let ownerInfo = null;
      if (venueData.owner_id) {
        ownerInfo = owners.find(o => o.id === venueData.owner_id);
      }
      
      setEditingVenue({ ...venueData, ownerInfo });
      setVenueEditForm({
        name: venueData.name || "",
        venue_type: venueData.venue_type || "cafe",
        description: venueData.description || "",
        address: venueData.location?.address || "",
        city: venueData.location?.city || "",
        postcode: venueData.location?.postcode || "",
        neighborhood: venueData.neighborhood || "",
        lat: venueData.location?.lat || "",
        lng: venueData.location?.lng || "",
        wifi_quality: venueData.amenities?.wifi_quality || "good",
        noise_level: venueData.amenities?.noise_level || "moderate",
        power_outlets: venueData.amenities?.power_outlets || 5,
        has_meeting_rooms: venueData.amenities?.has_meeting_rooms || false,
        has_parking: venueData.amenities?.has_parking || false,
        has_food: venueData.amenities?.has_food || false,
        has_drinks: venueData.amenities?.has_drinks || true,
        // New granular amenities
        long_stay_friendly: venueData.amenities?.long_stay_friendly || false,
        good_for_calls: venueData.amenities?.good_for_calls || false,
        has_outdoor_seating: venueData.amenities?.has_outdoor_seating || false,
        has_ac: venueData.amenities?.has_ac || false,
        is_spacious: venueData.amenities?.is_spacious || false,
        natural_light: venueData.amenities?.natural_light || false,
        pet_friendly: venueData.amenities?.pet_friendly || false,
        wheelchair_accessible: venueData.amenities?.wheelchair_accessible || false,
        group_friendly: venueData.amenities?.group_friendly || false,
        has_standing_desks: venueData.amenities?.has_standing_desks || false,
        coffee_avg: venueData.price_range?.coffee_avg || 0,
        food_avg: venueData.price_range?.food_avg || 0,
        opening_hours: venueData.opening_hours || "",
        is_premium: venueData.is_premium || false,
        is_verified: venueData.is_verified || false,
        rating: venueData.rating || 0,
        website: venueData.website || venueData.contact_details?.website || "",
        phone: venueData.phone || venueData.contact_details?.phone || "",
        email: venueData.contact_details?.email || "",
        // External booking links
        external_booking: venueData.external_booking || {
          opentable: "",
          thefork: "",
          booksy: "",
          booking_ninja: "",
          google_reserve: "",
          resy: "",
          yelp_reservations: "",
          custom_booking_url: ""
        },
        // Best times to work
        best_times: venueData.best_times || {
          quiet_hours: "",
          peak_hours: "",
          busiest_day: ""
        },
        // CMS fields
        images: venueData.images || [],
        custom_logo_url: venueData.custom_logo_url || "",
        branding: venueData.branding || {
          primary_color: "#1e3a5f",
          secondary_color: "#57c5b6",
          accent_color: "#159895",
          text_color: "#ffffff"
        },
        workspace_booking: venueData.workspace_booking || {
          enabled: false,
          total_spaces: 10,
          notification_email: true,
          notification_app: true
        },
        // Owner fields
        owner_id: venueData.owner_id || ""
      });
      setShowVenueEditModal(true);
    } catch (error) {
      toast.error("Failed to load venue details");
    }
  };

  const handleSaveVenueEdit = async () => {
    if (!editingVenue) return;
    setProcessing(editingVenue.id);
    
    try {
      // Parse lat/lng - handle both string and number inputs
      const parsedLat = typeof venueEditForm.lat === 'number' ? venueEditForm.lat : parseFloat(venueEditForm.lat);
      const parsedLng = typeof venueEditForm.lng === 'number' ? venueEditForm.lng : parseFloat(venueEditForm.lng);
      
      const updateData = {
        name: venueEditForm.name,
        venue_type: venueEditForm.venue_type,
        description: venueEditForm.description,
        location: {
          address: venueEditForm.address,
          city: venueEditForm.city,
          postcode: venueEditForm.postcode,
          lat: !isNaN(parsedLat) ? parsedLat : null,
          lng: !isNaN(parsedLng) ? parsedLng : null
        },
        amenities: {
          wifi_quality: venueEditForm.wifi_quality,
          noise_level: venueEditForm.noise_level,
          power_outlets: parseInt(venueEditForm.power_outlets) || 5,
          has_meeting_rooms: venueEditForm.has_meeting_rooms,
          has_parking: venueEditForm.has_parking,
          has_food: venueEditForm.has_food,
          has_drinks: venueEditForm.has_drinks,
          // New granular amenities
          long_stay_friendly: venueEditForm.long_stay_friendly || false,
          good_for_calls: venueEditForm.good_for_calls || false,
          has_outdoor_seating: venueEditForm.has_outdoor_seating || false,
          has_ac: venueEditForm.has_ac || false,
          is_spacious: venueEditForm.is_spacious || false,
          natural_light: venueEditForm.natural_light || false,
          pet_friendly: venueEditForm.pet_friendly || false,
          wheelchair_accessible: venueEditForm.wheelchair_accessible || false,
          group_friendly: venueEditForm.group_friendly || false,
          has_standing_desks: venueEditForm.has_standing_desks || false
        },
        price_range: {
          coffee_avg: parseInt(venueEditForm.coffee_avg) || 0,
          food_avg: parseInt(venueEditForm.food_avg) || 0
        },
        opening_hours: venueEditForm.opening_hours,
        neighborhood: venueEditForm.neighborhood || "",
        is_premium: venueEditForm.is_premium,
        is_verified: venueEditForm.is_verified,
        rating: parseFloat(venueEditForm.rating) || 0,
        website: venueEditForm.website,
        phone: venueEditForm.phone,
        contact_details: {
          email: venueEditForm.email,
          phone: venueEditForm.phone,
          website: venueEditForm.website
        },
        // External booking links
        external_booking: venueEditForm.external_booking || {},
        // Best times to work
        best_times: venueEditForm.best_times || {},
        // CMS fields
        images: venueEditForm.images || [],
        custom_logo_url: venueEditForm.custom_logo_url || "",
        branding: venueEditForm.branding,
        workspace_booking: venueEditForm.workspace_booking,
        // Owner assignment
        owner_id: venueEditForm.owner_id || null
      };
      
      await axios.patch(`${API}/admin/venues/${editingVenue.id}`, updateData);
      toast.success(`"${venueEditForm.name}" updated successfully`);
      setShowVenueEditModal(false);
      setEditingVenue(null);
      fetchAllData();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to update venue");
    } finally {
      setProcessing(null);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
            <p className="text-gray-500 mt-2">Strider Management</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand" data-testid="admin-password-input" />
              </div>
              {loginError && <p className="mt-2 text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {loginError}</p>}
            </div>
            <button type="submit" className="w-full btn-primary py-3" data-testid="admin-login-btn">Login to Admin Panel</button>
          </form>
          <button onClick={() => navigate("/")} className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm">← Back to Website</button>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          {/* Mobile: Stack header content */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-brand-light" />
              <div>
                <h1 className="font-semibold text-lg">Strider Admin</h1>
                <p className="text-xs text-gray-400 hidden sm:block">Auto-refreshes every minute</p>
              </div>
            </div>
            
            {/* Action Buttons - Scrollable on mobile */}
            <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar admin-header-actions">
              {/* Notification toggle */}
              <button 
                onClick={() => {
                  if (notificationPermission !== "granted") {
                    Notification.requestPermission().then(permission => {
                      setNotificationPermission(permission);
                      if (permission === "granted") {
                        toast.success("Notifications enabled!");
                        sendNotification("Notifications enabled", "You'll be notified of new pending items");
                      }
                    });
                  } else {
                    toast.info("Notifications are enabled");
                  }
                }}
                className={`p-2.5 rounded-lg relative flex-shrink-0 touch-target-sm ${
                  notificationPermission === "granted" 
                    ? "bg-green-500/20 text-green-400" 
                    : "hover:bg-white/10 text-gray-400"
                }`}
                title={notificationPermission === "granted" ? "Notifications enabled" : "Enable notifications"}
                data-testid="notification-btn"
              >
                {notificationPermission === "granted" ? (
                  <BellRing className="w-5 h-5" />
                ) : (
                  <BellOff className="w-5 h-5" />
                )}
                {lastPendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {lastPendingCount > 9 ? "9+" : lastPendingCount}
                  </span>
                )}
              </button>
              
              {/* Site Links */}
              <div className="flex items-center gap-1 border-r border-white/20 pr-2 mr-1 flex-shrink-0">
                <a 
                  href="/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 p-2.5 hover:bg-white/10 rounded-lg text-sm transition-colors touch-target-sm"
                  title="View Main Site"
                  data-testid="view-desktop-site-link"
                >
                  <Monitor className="w-4 h-4" />
                  <span className="hidden lg:inline">Site</span>
                </a>
              </div>
              
              {/* Moderation Link */}
              <button 
                onClick={() => navigate('/admin/moderation')}
                className="flex items-center gap-1.5 px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-sm transition-colors flex-shrink-0 touch-target-sm"
                title="Content Moderation"
                data-testid="moderation-link"
              >
                <Flag className="w-4 h-4" />
                <span className="hidden sm:inline">Moderation</span>
              </button>
              
              {/* Install App Button */}
              {!isStandalone && (
                <button 
                  onClick={handleInstallClick}
                  className="flex items-center gap-1.5 px-3 py-2.5 bg-brand hover:bg-brand-dark rounded-lg text-sm transition-colors flex-shrink-0 touch-target-sm"
                  title="Install Admin as app"
                  data-testid="install-app-btn"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Install</span>
                </button>
              )}
              
              <button 
                onClick={fetchAllData} 
                disabled={loading} 
                className="p-2.5 hover:bg-white/10 rounded-lg flex-shrink-0 touch-target-sm" 
                title="Refresh now" 
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              
              <button 
                onClick={handleLogout} 
                className="flex items-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg flex-shrink-0 touch-target-sm" 
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{submissions.length}</p><p className="text-xs text-gray-500">Pending</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><MapPin className="w-5 h-5 text-green-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{venues.length}</p><p className="text-xs text-gray-500">Venues</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-purple-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{venues.filter(v => v.is_premium).length}</p><p className="text-xs text-gray-500">Premium</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{invoices.length}</p><p className="text-xs text-gray-500">Invoices</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center"><Building2 className="w-5 h-5 text-teal-600" /></div>
              <div><p className="text-2xl font-bold text-gray-900">{inquiries.length}</p><p className="text-xs text-gray-500">Inquiries</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Info Banner */}
      <div className="max-w-7xl mx-auto px-4 pb-4">
        <div className="bg-gradient-to-r from-brand to-brand-dark text-white rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><PoundSterling className="w-5 h-5" /> Premium Listing: £{pricing.premium_annual}/year</h3>
            <p className="text-sm text-white/80 mt-1">Featured placement • 5 Photos • Laptop Friendly badge • Website link</p>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href="/admin/moderation" 
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm font-medium transition-colors"
              data-testid="moderation-promo-link"
            >
              <Shield className="w-4 h-4" />
              Review Moderation
            </a>
            <div className="text-sm text-white/70">
              Free: Basic listing, 2 photos
            </div>
          </div>
        </div>
      </div>

      {/* Tabs - Horizontally scrollable on mobile */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg p-1.5 shadow-sm">
          <div className="scroll-tabs hide-scrollbar">
            {[
              { id: "analytics", label: "Analytics" },
              { id: "submissions", label: `Pending (${submissions.length})` },
              { id: "reviews", label: `Reviews (${reviewsTotal})` },
              { id: "error-reports", label: `Reports (${errorReports.filter(r => r.status === 'pending').length})` },
              { id: "venues", label: `Venues (${venues.length})` },
              { id: "owners", label: "Owners CMS" },
              { id: "contributors", label: "Contributors" },
              { id: "leaderboard", label: <span className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Leaderboard</span> },
              { id: "geography", label: <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> Locations</span> },
              { id: "vouchers", label: <span className="flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> Vouchers</span> },
              { id: "events", label: <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Events</span> },
              { id: "invoices", label: `Invoices (${invoices.length})` },
              { id: "inquiries", label: `Corporate (${inquiries.length})` },
              { id: "system", label: <span className="flex items-center gap-1.5"><Bug className="w-3.5 h-3.5" /> System {errorStats?.unresolved > 0 && <span className="w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{errorStats.unresolved > 9 ? '9+' : errorStats.unresolved}</span>}</span> },
              { id: "settings", label: "Settings" },
            ].map(tab => (
              <button 
                key={tab.id} 
                onClick={() => setActiveTab(tab.id)} 
                className={`tab-btn ${activeTab === tab.id ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-100"}`} 
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Mobile scroll hint */}
          <p className="text-xs text-gray-400 text-center mt-1 md:hidden">Swipe to see more tabs →</p>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white" data-testid="admin-search-input" />
          </div>
          {activeTab === "invoices" && (
            <button onClick={() => setShowInvoiceForm(true)} className="btn-primary flex items-center gap-2" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4" /> New Invoice
            </button>
          )}
          {activeTab === "vouchers" && (
            <button onClick={() => setShowVoucherForm(true)} className="btn-primary flex items-center gap-2" data-testid="create-voucher-btn">
              <Plus className="w-4 h-4" /> New Voucher
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <RefreshCw className="w-8 h-8 text-brand animate-spin mx-auto mb-3" />
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : activeTab === "analytics" ? (
          /* Analytics Tab */
          <div>
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-6 h-6 text-brand" />
              <h2 className="text-xl font-semibold text-gray-900">Platform Analytics</h2>
            </div>
            <AdminAnalytics />
          </div>
        ) : activeTab === "submissions" ? (
          <AdminSubmissionsTab
            filteredSubmissions={filteredSubmissions}
            sortConfig={sortConfig}
            handleSort={handleSort}
            expandedItem={expandedItem}
            setExpandedItem={setExpandedItem}
            processing={processing}
            handleApprove={handleApprove}
            openRejectModal={openRejectModal}
          />
        ) : activeTab === "reviews" ? (
          <AdminReviewsTab
            reviewVenues={reviewVenues}
            filteredReviewVenues={filteredReviewVenues}
            reviewsTotal={reviewsTotal}
            reviewFilters={reviewFilters}
            setReviewFilters={setReviewFilters}
            reviewsLoading={reviewsLoading}
            expandedVenues={expandedVenues}
            setExpandedVenues={setExpandedVenues}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            fetchReviews={fetchReviews}
            handleReviewStatusChange={handleReviewStatusChange}
            handleDeleteReview={handleDeleteReview}
            pendingSubmissionReviews={pendingSubmissionReviews}
            approveSubmissionReview={approveSubmissionReview}
            rejectSubmissionReview={rejectSubmissionReview}
          />
        ) : activeTab === "error-reports" ? (
          <AdminErrorReportsTab
            filteredErrorReports={filteredErrorReports}
            sortConfig={sortConfig}
            handleSort={handleSort}
            handleResolveErrorReport={handleResolveErrorReport}
            processing={processing}
          />
        ) : activeTab === "venues" ? (
          /* Venues Tab */
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5" /> Venues
                </h3>
                <p className="text-sm text-gray-500 mt-1">{filteredVenues.length} venues</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddVenueModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
                  data-testid="add-venue-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Venue</span>
                  <span className="sm:hidden">Add</span>
                </button>
              </div>
            </div>
            {/* Mobile hint */}
            <div className="px-4 py-2 bg-blue-50 text-blue-700 text-xs md:hidden flex items-center gap-2">
              <span>Swipe left to see all actions</span>
              <span className="text-blue-400">→</span>
            </div>
            {/* Scrollable table container */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50 border-b"><tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('venues', 'name')}
                >
                  <div className="flex items-center gap-1">
                    Venue
                    {sortConfig.venues?.field === 'name' && (
                      sortConfig.venues.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('venues', 'location.city')}
                >
                  <div className="flex items-center gap-1">
                    City
                    {sortConfig.venues?.field === 'location.city' && (
                      sortConfig.venues.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('venues', 'wifi_speed_mbps')}
                >
                  <div className="flex items-center justify-center gap-1">
                    WiFi
                    {sortConfig.venues?.field === 'wifi_speed_mbps' && (
                      sortConfig.venues.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('venues', 'is_premium')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Status
                    {sortConfig.venues?.field === 'is_premium' && (
                      sortConfig.venues.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVenues.map((venue) => (
                  <tr key={venue.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100"><img src={getVenueImage(venue, 100)} alt="" className="w-full h-full object-cover" /></div>
                        <div className="min-w-0"><p className="font-medium text-gray-900 truncate">{venue.name}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{venue.location?.city}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        {venue.wifi_speed_mbps ? (
                          <div className="text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              venue.wifi_speed_mbps >= 100 ? 'bg-teal-100 text-teal-700' :
                              venue.wifi_speed_mbps >= 50 ? 'bg-green-100 text-green-700' :
                              venue.wifi_speed_mbps >= 25 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              <Wifi className="w-3 h-3" />
                              {venue.wifi_speed_mbps} Mbps
                            </span>
                            {venue.wifi_last_tested && (
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {new Date(venue.wifi_last_tested).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Not tested</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {venue.is_premium ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">Premium</span> : <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Free</span>}
                        {venue.is_verified && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Verified</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={(e) => { e.stopPropagation(); window.open(`/venue/${venue.id}`, '_blank'); }} className="action-btn text-gray-400 hover:text-brand hover:bg-brand/10" title="View"><Eye className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.stopPropagation(); openVenueEdit(venue); }} className="action-btn text-gray-400 hover:text-blue-600 hover:bg-blue-50" title="Edit Venue" data-testid={`edit-venue-${venue.id}`}><Edit className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleWiFiTest(venue.id, venue.name); }} disabled={processing === venue.id} className="action-btn text-gray-400 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-50" title="WiFi Test" data-testid={`wifi-test-${venue.id}`}><Wifi className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleReGeocode(venue.id, venue.name); }} disabled={processing === venue.id} className="action-btn text-gray-400 hover:text-green-600 hover:bg-green-50 disabled:opacity-50" title="Re-geocode" data-testid={`geocode-venue-${venue.id}`}><MapPin className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteVenue(venue.id, venue.name); }} disabled={processing === venue.id} className="action-btn text-red-500 hover:text-white hover:bg-red-500 disabled:opacity-50" title="Delete" data-testid={`delete-venue-${venue.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {filteredVenues.length === 0 && <div className="p-8 text-center text-gray-500">No venues found</div>}
          </div>
        ) : activeTab === "owners" ? (
          <AdminOwnersTab
            owners={owners}
            filteredOwners={filteredOwners}
            expandedOwners={expandedOwners}
            setExpandedOwners={setExpandedOwners}
            processing={processing}
            setShowAddOwnerModal={setShowAddOwnerModal}
            handleDeleteOwner={handleDeleteOwner}
            sortConfig={sortConfig.owners}
            onSort={(field) => handleSort('owners', field)}
          />
        ) : activeTab === "contributors" ? (
          <AdminContributorsTab
            contributors={contributors}
            filteredContributors={filteredContributors}
            contributorStats={contributorStats}
            sortConfig={sortConfig}
            handleSort={handleSort}
            onAddContributor={() => setShowAddContributorModal(true)}
            onDeleteContributor={(contrib) => setDeleteContributorModal({ show: true, contributor: contrib })}
          />
        ) : activeTab === "leaderboard" ? (
          <AdminLeaderboardTab adminPassword={ADMIN_PASSWORD} />
        ) : activeTab === "geography" ? (
          <AdminGeographyTab
            countries={countries}
            cities={cities}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            showCountryModal={showCountryModal}
            setShowCountryModal={setShowCountryModal}
            showCityModal={showCityModal}
            setShowCityModal={setShowCityModal}
            editingCountry={editingCountry}
            setEditingCountry={setEditingCountry}
            editingCity={editingCity}
            setEditingCity={setEditingCity}
            countryForm={countryForm}
            setCountryForm={setCountryForm}
            cityForm={cityForm}
            setCityForm={setCityForm}
            newHighlight={newHighlight}
            setNewHighlight={setNewHighlight}
            newVariation={newVariation}
            setNewVariation={setNewVariation}
            handleSaveCountry={handleSaveCountry}
            handleSaveCity={handleSaveCity}
            handleDeleteCountry={handleDeleteCountry}
            handleDeleteCity={handleDeleteCity}
            openEditCountry={openEditCountry}
            openEditCity={openEditCity}
            openNewCity={openNewCity}
            seedGeographyData={seedGeographyData}
          />
        ) : activeTab === "invoices" ? (
          <AdminInvoicesTab invoices={invoices} pricing={pricing} fetchAllData={fetchAllData} />
        ) : activeTab === "vouchers" ? (
          <AdminVouchersTab vouchers={vouchers} fetchAllData={fetchAllData} />
        ) : activeTab === "events" ? (
          <AdminEventsTab
            communityEvents={communityEvents}
            onAddEvent={() => {
              setEditingEvent(null);
              setEventForm({ title: "", description: "", location: "", city: "", date: "", time: "19:00", recurring: "", max_attendees: null, image_url: "", is_active: true });
              setShowEventModal(true);
            }}
            onEditEvent={(event) => {
              setEditingEvent(event);
              setEventForm({
                title: event.title,
                description: event.description || "",
                location: event.location,
                city: event.city,
                date: event.date?.split('T')[0] || "",
                time: event.time || "19:00",
                recurring: event.recurring || "",
                max_attendees: event.max_attendees || null,
                image_url: event.image_url || "",
                is_active: event.is_active
              });
              setShowEventModal(true);
            }}
            onDeleteEvent={deleteEvent}
          />
        ) : activeTab === "inquiries" ? (
          <AdminInquiriesTab
            inquiries={inquiries}
            onCreateInvoice={(inq) => {
              setInvoiceForm({ 
                company_name: inq.company_name, 
                contact_name: inq.contact_name, 
                email: inq.email, 
                address: "", 
                venue_count: parseInt(inq.venue_count?.split("-")[0]) || 1, 
                discount_percent: 0, 
                notes: "" 
              });
              setShowInvoiceForm(true);
              setActiveTab("invoices");
            }}
          />
        ) : activeTab === "system" ? (
          <AdminSystemTab
            systemHealth={systemHealth}
            errorStats={errorStats}
            systemErrors={systemErrors}
            aiTestingStatus={aiTestingStatus}
            aiTestHistory={aiTestHistory}
            aiAutoFixes={aiAutoFixes}
            latestTestRun={latestTestRun}
            e2eTestReports={e2eTestReports}
            selectedE2eReport={selectedE2eReport}
            setSelectedE2eReport={setSelectedE2eReport}
            fetchAllData={fetchAllData}
            fetchE2eReports={fetchE2eReports}
          />
        ) : (
          <AdminSettingsTab pricing={pricing} fetchAllData={fetchAllData} />
        )}
      </div>
      
      {/* Rejection Reason Modal */}
      <AnimatePresence>
        {rejectModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setRejectModal({ open: false, submissionId: null, name: '' })}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Reject "{rejectModal.name}"
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Please provide a reason for rejection. This will be sent to the submitter.
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., The venue doesn't appear to have WiFi available for customers, or the address couldn't be verified..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                data-testid="rejection-reason-input"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setRejectModal({ open: false, submissionId: null, name: '' })}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReject({ id: rejectModal.submissionId, name: rejectModal.name }, rejectionReason)}
                  disabled={processing === rejectModal.submissionId}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50"
                  data-testid="confirm-reject-btn"
                >
                  {processing === rejectModal.submissionId ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Venue Edit Modal */}
      <AnimatePresence>
        {showVenueEditModal && editingVenue && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center overflow-y-auto p-2 sm:p-4"
            onClick={() => setShowVenueEditModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-auto max-h-[95vh] flex flex-col"
            >
              <div className="p-4 sm:p-6 border-b border-gray-100 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Edit className="w-5 h-5 text-brand" />
                  Edit Venue
                </h2>
                <p className="text-sm text-gray-500 mt-1">Update any venue details</p>
              </div>
              
              <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Basic Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Venue Name</label>
                      <input
                        type="text"
                        value={venueEditForm.name}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, name: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        data-testid="edit-venue-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Venue Type</label>
                      <select
                        value={venueEditForm.venue_type}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, venue_type: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        data-testid="edit-venue-type"
                      >
                        <option value="cafe">Cafe</option>
                        <option value="pub">Pub</option>
                        <option value="hotel">Hotel</option>
                        <option value="library">Library</option>
                        <option value="hub">Coworking Hub</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Opening Hours</label>
                      <input
                        type="text"
                        value={venueEditForm.opening_hours}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, opening_hours: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="Mon-Fri: 8am-6pm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <textarea
                        value={venueEditForm.description}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Owner Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Owner / Claimed By
                  </h3>
                  {editingVenue?.ownerInfo ? (
                    <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                            <span className="text-purple-700 font-bold text-lg">
                              {editingVenue.ownerInfo.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{editingVenue.ownerInfo.name}</p>
                            <p className="text-sm text-gray-600">{editingVenue.ownerInfo.email}</p>
                            {editingVenue.ownerInfo.company && (
                              <p className="text-xs text-gray-500">{editingVenue.ownerInfo.company}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            editingVenue.ownerInfo.subscription_tier === 'premium' 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {editingVenue.ownerInfo.subscription_tier === 'premium' && <Crown className="w-3 h-3" />}
                            {editingVenue.ownerInfo.subscription_tier || 'free'}
                          </span>
                          {editingVenue.ownerInfo.phone && (
                            <p className="text-xs text-gray-500 mt-1">{editingVenue.ownerInfo.phone}</p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-purple-200 flex items-center justify-between">
                        <p className="text-xs text-purple-600">
                          Owner ID: {editingVenue.ownerInfo.id}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Remove owner from this venue? The owner will no longer manage this venue.')) {
                              setVenueEditForm({ ...venueEditForm, owner_id: "" });
                              setEditingVenue({ ...editingVenue, ownerInfo: null });
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          Remove Owner
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center">
                        <p className="text-gray-500 text-sm mb-2">No owner assigned to this venue</p>
                        <p className="text-xs text-gray-400">Select an owner below to assign</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Assign Owner</label>
                        <select
                          value={venueEditForm.owner_id || ""}
                          onChange={(e) => {
                            const selectedOwner = owners.find(o => o.id === e.target.value);
                            setVenueEditForm({ ...venueEditForm, owner_id: e.target.value });
                            if (selectedOwner) {
                              setEditingVenue({ ...editingVenue, ownerInfo: selectedOwner });
                            }
                          }}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        >
                          <option value="">-- Select Owner --</option>
                          {owners.map(owner => (
                            <option key={owner.id} value={owner.id}>
                              {owner.name} ({owner.email}) {owner.subscription_tier === 'premium' ? '⭐' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Location
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                      <input
                        type="text"
                        value={venueEditForm.address}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, address: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                      <input
                        type="text"
                        value={venueEditForm.city}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, city: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Postcode</label>
                      <input
                        type="text"
                        value={venueEditForm.postcode}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, postcode: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Neighborhood</label>
                      <input
                        type="text"
                        value={venueEditForm.neighborhood || ""}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, neighborhood: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="e.g., Shoreditch, Soho"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={venueEditForm.lat}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow numbers, decimal point, and minus sign
                          if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) {
                            setVenueEditForm({ ...venueEditForm, lat: val });
                          }
                        }}
                        placeholder="e.g., 51.5074"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={venueEditForm.lng}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow numbers, decimal point, and minus sign
                          if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) {
                            setVenueEditForm({ ...venueEditForm, lng: val });
                          }
                        }}
                        placeholder="e.g., -0.1278"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Amenities */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Wifi className="w-4 h-4" /> Amenities
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">WiFi Quality</label>
                      <select
                        value={venueEditForm.wifi_quality}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, wifi_quality: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      >
                        <option value="very_fast">Very Fast</option>
                        <option value="fast">Fast</option>
                        <option value="good">Good</option>
                        <option value="poor">Poor</option>
                        <option value="very_poor">Very Poor</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Noise Level</label>
                      <select
                        value={venueEditForm.noise_level}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, noise_level: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      >
                        <option value="silent">Silent</option>
                        <option value="quiet">Quiet</option>
                        <option value="moderate">Moderate</option>
                        <option value="lively">Lively</option>
                        <option value="loud">Loud</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Power Outlets</label>
                      <input
                        type="number"
                        min="0"
                        value={venueEditForm.power_outlets}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, power_outlets: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Coffee Price Level (1-5)</label>
                      <select
                        value={venueEditForm.coffee_avg || 0}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, coffee_avg: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      >
                        <option value={0}>Not Set</option>
                        <option value={1}>£ - Very Affordable</option>
                        <option value={2}>££ - Budget Friendly</option>
                        <option value={3}>£££ - Moderate</option>
                        <option value={4}>££££ - Pricey</option>
                        <option value={5}>£££££ - Premium</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Food Price Level (1-5)</label>
                      <select
                        value={venueEditForm.food_avg || 0}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, food_avg: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      >
                        <option value={0}>Not Set</option>
                        <option value={1}>£ - Very Affordable</option>
                        <option value={2}>££ - Budget Friendly</option>
                        <option value={3}>£££ - Moderate</option>
                        <option value={4}>££££ - Pricey</option>
                        <option value={5}>£££££ - Premium</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { key: "has_meeting_rooms", label: "Meeting Rooms", icon: Users },
                      { key: "has_parking", label: "Parking", icon: Car },
                      { key: "has_food", label: "Food", icon: UtensilsCrossed },
                      { key: "has_drinks", label: "Drinks", icon: GlassWater }
                    ].map(({ key, label, icon: Icon }) => (
                      <label key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${venueEditForm[key] ? "bg-green-50 border-green-300" : "border-gray-200"}`}>
                        <input
                          type="checkbox"
                          checked={venueEditForm[key]}
                          onChange={(e) => setVenueEditForm({ ...venueEditForm, [key]: e.target.checked })}
                          className="sr-only"
                        />
                        <Icon className={`w-4 h-4 ${venueEditForm[key] ? "text-green-600" : "text-gray-400"}`} />
                        <span className={`text-sm ${venueEditForm[key] ? "text-green-700" : "text-gray-600"}`}>{label}</span>
                      </label>
                    ))}
                  </div>
                  {/* New granular amenities */}
                  <p className="text-xs text-gray-500 mt-2">Work-Friendly Features</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "long_stay_friendly", label: "Long Stays" },
                      { key: "good_for_calls", label: "Good for Calls" },
                      { key: "has_outdoor_seating", label: "Outdoor" },
                      { key: "has_ac", label: "A/C" },
                      { key: "is_spacious", label: "Spacious" },
                      { key: "natural_light", label: "Natural Light" },
                      { key: "pet_friendly", label: "Pets OK" },
                      { key: "wheelchair_accessible", label: "Accessible" },
                      { key: "group_friendly", label: "Groups OK" },
                      { key: "has_standing_desks", label: "Standing Desks" }
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setVenueEditForm({ ...venueEditForm, [key]: !venueEditForm[key] });
                        }}
                        className={`px-2 py-1 rounded-lg border text-xs ${venueEditForm[key] ? "bg-brand/10 border-brand text-brand" : "border-gray-200 text-gray-500"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* PREMIUM ONLY: External Booking & Contact */}
                {venueEditForm.is_premium ? (
                  <>
                    {/* Booking System - Dropdown + URL (Premium Only) */}
                    <div className="space-y-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Booking System
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Premium</span>
                      </h3>
                      <p className="text-xs text-amber-700">Select your booking platform and add the reservation URL</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Booking Platform</label>
                          <select
                            value={venueEditForm.external_booking?.platform || ""}
                            onChange={(e) => setVenueEditForm({
                              ...venueEditForm,
                              external_booking: { ...venueEditForm.external_booking, platform: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          >
                            <option value="">No booking system</option>
                            <option value="opentable">OpenTable</option>
                            <option value="thefork">TheFork</option>
                            <option value="booksy">Booksy</option>
                            <option value="booking_ninja">Booking Ninja</option>
                            <option value="google_reserve">Reserve with Google</option>
                            <option value="resy">Resy</option>
                            <option value="yelp_reservations">Yelp Reservations</option>
                            <option value="custom">Other / Custom</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Booking URL</label>
                          <input
                            type="url"
                            value={venueEditForm.external_booking?.booking_url || ""}
                            onChange={(e) => setVenueEditForm({
                              ...venueEditForm,
                              external_booking: { ...venueEditForm.external_booking, booking_url: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            placeholder="https://..."
                            disabled={!venueEditForm.external_booking?.platform}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Best Times to Work (Premium Only) */}
                    <div className="space-y-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Best Times to Work
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Premium</span>
                      </h3>
                      <p className="text-xs text-amber-700">Help users know when it's quietest</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Quiet Hours</label>
                          <input
                            type="text"
                            value={venueEditForm.best_times?.quiet_hours || ""}
                            onChange={(e) => setVenueEditForm({
                              ...venueEditForm,
                              best_times: { ...venueEditForm.best_times, quiet_hours: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            placeholder="e.g., 9am-12pm, 2pm-5pm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Peak Hours</label>
                          <input
                            type="text"
                            value={venueEditForm.best_times?.peak_hours || ""}
                            onChange={(e) => setVenueEditForm({
                              ...venueEditForm,
                              best_times: { ...venueEditForm.best_times, peak_hours: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            placeholder="e.g., 12pm-2pm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Busiest Day</label>
                          <select
                            value={venueEditForm.best_times?.busiest_day || ""}
                            onChange={(e) => setVenueEditForm({
                              ...venueEditForm,
                              best_times: { ...venueEditForm.best_times, busiest_day: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          >
                            <option value="">Not Set</option>
                            <option value="Monday">Monday</option>
                            <option value="Tuesday">Tuesday</option>
                            <option value="Wednesday">Wednesday</option>
                            <option value="Thursday">Thursday</option>
                            <option value="Friday">Friday</option>
                            <option value="Saturday">Saturday</option>
                            <option value="Sunday">Sunday</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Contact (Premium Only) */}
                    <div className="space-y-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                        <Globe className="w-4 h-4" /> Contact Details
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Premium</span>
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
                          <input
                            type="url"
                            value={venueEditForm.website || ""}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, website: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={venueEditForm.phone || ""}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, phone: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            placeholder="Phone number"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                          <input
                            type="email"
                            value={venueEditForm.email || ""}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, email: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            placeholder="contact@venue.com"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Booking, Best Times & Contact</span> are available for Premium venues only.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Toggle "Premium" above to enable these features.
                    </p>
                  </div>
                )}

                {/* Venue Images */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Venue Images
                  </h3>
                  <p className="text-xs text-gray-500">Upload images or add URLs. Images are automatically optimized.</p>
                  
                  {/* File Upload Section */}
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-brand transition-colors">
                    <input
                      type="file"
                      id="venue-image-upload"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        
                        toast.info(`Uploading ${files.length} image(s)...`);
                        const uploadedUrls = [];
                        
                        for (const file of files) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await axios.post(`${API}/admin/upload-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            
                            if (response.data.success) {
                              // Use the full URL
                              const fullUrl = `${BACKEND_URL}${response.data.url}`;
                              uploadedUrls.push(fullUrl);
                            }
                          } catch (err) {
                            toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
                          }
                        }
                        
                        if (uploadedUrls.length > 0) {
                          const currentImages = venueEditForm.images || [];
                          setVenueEditForm({ ...venueEditForm, images: [...currentImages, ...uploadedUrls] });
                          toast.success(`Uploaded ${uploadedUrls.length} image(s)`);
                        }
                        
                        e.target.value = ''; // Reset input
                      }}
                      data-testid="venue-image-upload-input"
                    />
                    <label htmlFor="venue-image-upload" className="cursor-pointer">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <Image className="w-5 h-5 text-gray-400" />
                        </div>
                        <span className="text-sm text-gray-600">Click to upload images</span>
                        <span className="text-xs text-gray-400">JPG, PNG, WebP, GIF (max 5MB each)</span>
                      </div>
                    </label>
                  </div>
                  
                  {/* Current Images Preview */}
                  {venueEditForm.images?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {venueEditForm.images.map((img, idx) => (
                        <div key={`img-${img.substring(0, 50)}-${idx}`} className="relative group">
                          <img 
                            src={img} 
                            alt={`Venue ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=100'; }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = [...venueEditForm.images];
                              newImages.splice(idx, 1);
                              setVenueEditForm({ ...venueEditForm, images: newImages });
                            }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Image URL Input */}
                  <div className="space-y-2">
                    <textarea
                      value={(venueEditForm.images || []).join('\n')}
                      onChange={(e) => {
                        const urls = e.target.value.split('\n').filter(url => url.trim());
                        setVenueEditForm({ ...venueEditForm, images: urls });
                      }}
                      rows={4}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                      placeholder="https://images.unsplash.com/photo-xxx?w=800&#10;https://i.imgur.com/xxx.jpg&#10;..."
                      data-testid="edit-venue-images"
                    />
                    <p className="text-xs text-gray-400">
                      {venueEditForm.images?.length || 0} image{(venueEditForm.images?.length || 0) !== 1 ? 's' : ''} • Tip: Use ?w=800 for Unsplash URLs to optimize size
                    </p>
                  </div>
                  
                  {/* Quick Add Suggestions */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500">Quick add:</span>
                    {[
                      { label: "Cafe", url: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800" },
                      { label: "Pub", url: "https://images.unsplash.com/photo-1772365833557-366777652946?w=800" },
                      { label: "Laptop+Coffee", url: "https://images.unsplash.com/photo-1528058021068-723d4824a62e?w=800" },
                    ].map(({ label, url }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          const currentImages = venueEditForm.images || [];
                          if (!currentImages.includes(url)) {
                            setVenueEditForm({ ...venueEditForm, images: [...currentImages, url] });
                          }
                        }}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        + {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Status & Verification */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Settings className="w-4 h-4" /> Status & Ratings
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Rating (0-10)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={venueEditForm.rating}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, rating: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer flex-1 min-w-[120px] justify-center ${venueEditForm.is_premium ? "bg-amber-50 border-amber-300" : "border-gray-200"}`}>
                      <input
                        type="checkbox"
                        checked={venueEditForm.is_premium}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, is_premium: e.target.checked })}
                        className="sr-only"
                      />
                      <Crown className={`w-4 h-4 ${venueEditForm.is_premium ? "text-amber-600" : "text-gray-400"}`} />
                      <span className={`text-sm font-medium ${venueEditForm.is_premium ? "text-amber-700" : "text-gray-600"}`}>Premium</span>
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer flex-1 min-w-[120px] justify-center ${venueEditForm.is_verified ? "bg-green-50 border-green-300" : "border-gray-200"}`}>
                      <input
                        type="checkbox"
                        checked={venueEditForm.is_verified}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, is_verified: e.target.checked })}
                        className="sr-only"
                      />
                      <Shield className={`w-4 h-4 ${venueEditForm.is_verified ? "text-green-600" : "text-gray-400"}`} />
                      <span className={`text-sm font-medium ${venueEditForm.is_verified ? "text-green-700" : "text-gray-600"}`}>Verified</span>
                    </label>
                  </div>
                </div>

                {/* CMS: Images */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Images & Logo
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Custom Logo URL</label>
                      <input
                        type="url"
                        value={venueEditForm.custom_logo_url || ""}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, custom_logo_url: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="https://example.com/logo.png"
                      />
                      {venueEditForm.custom_logo_url && (
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                          <img src={venueEditForm.custom_logo_url} alt="Logo preview" className="h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Image URLs (one per line)</label>
                      <textarea
                        value={(venueEditForm.images || []).join('\n')}
                        onChange={(e) => setVenueEditForm({ ...venueEditForm, images: e.target.value.split('\n').filter(url => url.trim()) })}
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none font-mono"
                        placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                      />
                      <p className="text-xs text-gray-500 mt-1">{(venueEditForm.images || []).length} image(s) configured</p>
                    </div>
                  </div>
                </div>

                {/* CMS: Branding (Premium) */}
                {venueEditForm.is_premium && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Palette className="w-4 h-4" /> Branding Colors
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Premium</span>
                    </h3>
                    <div className="p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${venueEditForm.branding?.primary_color || '#1e3a5f'}, ${venueEditForm.branding?.secondary_color || '#57c5b6'})` }}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                          <span style={{ color: venueEditForm.branding?.text_color || '#ffffff' }} className="text-sm font-bold">W</span>
                        </div>
                        <span style={{ color: venueEditForm.branding?.text_color || '#ffffff' }} className="font-semibold">{venueEditForm.name || 'Preview'}</span>
                      </div>
                      <button 
                        className="px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{ backgroundColor: venueEditForm.branding?.accent_color || '#159895', color: venueEditForm.branding?.text_color || '#ffffff' }}
                      >
                        Sample Button
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Primary Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={venueEditForm.branding?.primary_color || "#1e3a5f"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, primary_color: e.target.value } })}
                            className="w-10 h-10 rounded cursor-pointer border-0"
                          />
                          <input
                            type="text"
                            value={venueEditForm.branding?.primary_color || "#1e3a5f"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, primary_color: e.target.value } })}
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Secondary Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={venueEditForm.branding?.secondary_color || "#57c5b6"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, secondary_color: e.target.value } })}
                            className="w-10 h-10 rounded cursor-pointer border-0"
                          />
                          <input
                            type="text"
                            value={venueEditForm.branding?.secondary_color || "#57c5b6"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, secondary_color: e.target.value } })}
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Accent Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={venueEditForm.branding?.accent_color || "#159895"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, accent_color: e.target.value } })}
                            className="w-10 h-10 rounded cursor-pointer border-0"
                          />
                          <input
                            type="text"
                            value={venueEditForm.branding?.accent_color || "#159895"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, accent_color: e.target.value } })}
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={venueEditForm.branding?.text_color || "#ffffff"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, text_color: e.target.value } })}
                            className="w-10 h-10 rounded cursor-pointer border-0"
                          />
                          <input
                            type="text"
                            value={venueEditForm.branding?.text_color || "#ffffff"}
                            onChange={(e) => setVenueEditForm({ ...venueEditForm, branding: { ...venueEditForm.branding, text_color: e.target.value } })}
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* CMS: Workspace Booking (Premium) */}
                {venueEditForm.is_premium && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Workspace Booking
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Premium</span>
                    </h3>
                    <div className="space-y-4">
                      <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer ${venueEditForm.workspace_booking?.enabled ? "bg-green-50 border-green-300" : "border-gray-200"}`}>
                        <input
                          type="checkbox"
                          checked={venueEditForm.workspace_booking?.enabled || false}
                          onChange={(e) => setVenueEditForm({ 
                            ...venueEditForm, 
                            workspace_booking: { ...venueEditForm.workspace_booking, enabled: e.target.checked } 
                          })}
                          className="w-5 h-5 rounded border-gray-300 text-green-600"
                        />
                        <div>
                          <p className="font-medium text-gray-900">Enable Workspace Booking</p>
                          <p className="text-xs text-gray-500">Allow users to book workspace at this venue</p>
                        </div>
                      </label>
                      
                      {venueEditForm.workspace_booking?.enabled && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Total Spaces Available</label>
                            <input
                              type="number"
                              min="1"
                              value={venueEditForm.workspace_booking?.total_spaces || 10}
                              onChange={(e) => setVenueEditForm({ 
                                ...venueEditForm, 
                                workspace_booking: { ...venueEditForm.workspace_booking, total_spaces: parseInt(e.target.value) || 10 } 
                              })}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={venueEditForm.workspace_booking?.notification_email || false}
                                onChange={(e) => setVenueEditForm({ 
                                  ...venueEditForm, 
                                  workspace_booking: { ...venueEditForm.workspace_booking, notification_email: e.target.checked } 
                                })}
                                className="rounded border-gray-300"
                              />
                              Email notifications
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={venueEditForm.workspace_booking?.notification_app || false}
                                onChange={(e) => setVenueEditForm({ 
                                  ...venueEditForm, 
                                  workspace_booking: { ...venueEditForm.workspace_booking, notification_app: e.target.checked } 
                                })}
                                className="rounded border-gray-300"
                              />
                              App notifications
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 sm:p-6 border-t border-gray-100 flex gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowVenueEditModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveVenueEdit}
                  disabled={processing === editingVenue?.id}
                  className="flex-1 px-4 py-2.5 bg-brand hover:bg-brand-dark text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-venue-edit"
                >
                  {processing === editingVenue?.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4" /> Save Changes</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Add Venue Modal */}
        {showAddVenueModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddVenueModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-brand" /> Add New Venue
                </h2>
                <button onClick={() => setShowAddVenueModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name *</label>
                    <input type="text" value={newVenueForm.name} onChange={(e) => setNewVenueForm({...newVenueForm, name: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="e.g., The Coffee House" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select value={newVenueForm.venue_type} onChange={(e) => setNewVenueForm({...newVenueForm, venue_type: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200">
                      <option value="cafe">Cafe</option>
                      <option value="pub">Pub</option>
                      <option value="hotel">Hotel</option>
                      <option value="library">Library</option>
                      <option value="hub">Hub/Coworking</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input type="text" value={newVenueForm.city} onChange={(e) => setNewVenueForm({...newVenueForm, city: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="London" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input type="text" value={newVenueForm.address} onChange={(e) => setNewVenueForm({...newVenueForm, address: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="123 High Street" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input type="text" value={newVenueForm.postcode} onChange={(e) => setNewVenueForm({...newVenueForm, postcode: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="EC1A 1BB" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Neighborhood</label>
                    <input type="text" value={newVenueForm.neighborhood} onChange={(e) => setNewVenueForm({...newVenueForm, neighborhood: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="Shoreditch" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WiFi Quality</label>
                    <select value={newVenueForm.wifi_quality} onChange={(e) => setNewVenueForm({...newVenueForm, wifi_quality: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200">
                      <option value="very_fast">Very Fast</option>
                      <option value="fast">Fast</option>
                      <option value="good">Good</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Noise Level</label>
                    <select value={newVenueForm.noise_level} onChange={(e) => setNewVenueForm({...newVenueForm, noise_level: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200">
                      <option value="quiet">Quiet</option>
                      <option value="moderate">Moderate</option>
                      <option value="lively">Lively</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Power Outlets</label>
                    <input type="number" value={newVenueForm.power_outlets} onChange={(e) => setNewVenueForm({...newVenueForm, power_outlets: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" min="0" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Opening Hours</label>
                    <input type="text" value={newVenueForm.opening_hours} onChange={(e) => setNewVenueForm({...newVenueForm, opening_hours: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="Mon-Fri 8am-6pm" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={newVenueForm.description} onChange={(e) => setNewVenueForm({...newVenueForm, description: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" rows={2} placeholder="A great place to work..." />
                  </div>
                  <div className="col-span-2 flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={newVenueForm.is_premium} onChange={(e) => setNewVenueForm({...newVenueForm, is_premium: e.target.checked})} className="rounded" />
                      <span className="text-sm font-medium text-amber-600">Premium Venue</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={newVenueForm.has_food} onChange={(e) => setNewVenueForm({...newVenueForm, has_food: e.target.checked})} className="rounded" />
                      <span className="text-sm">Food Available</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={newVenueForm.has_parking} onChange={(e) => setNewVenueForm({...newVenueForm, has_parking: e.target.checked})} className="rounded" />
                      <span className="text-sm">Parking</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={newVenueForm.has_meeting_rooms} onChange={(e) => setNewVenueForm({...newVenueForm, has_meeting_rooms: e.target.checked})} className="rounded" />
                      <span className="text-sm">Meeting Rooms</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button onClick={() => setShowAddVenueModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={handleAddVenue} disabled={processing === "add-venue"} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark flex items-center gap-2">
                  {processing === "add-venue" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create Venue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Add Owner Modal */}
        {showAddOwnerModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddOwnerModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-md"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-brand" /> Add New Owner
                </h2>
                <button onClick={() => setShowAddOwnerModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={newOwnerForm.email} onChange={(e) => setNewOwnerForm({...newOwnerForm, email: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="owner@business.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
                  <input type="text" value={newOwnerForm.contact_name} onChange={(e) => setNewOwnerForm({...newOwnerForm, contact_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                  <input type="text" value={newOwnerForm.business_name} onChange={(e) => setNewOwnerForm({...newOwnerForm, business_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="The Coffee House Ltd" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={newOwnerForm.phone} onChange={(e) => setNewOwnerForm({...newOwnerForm, phone: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="+44 123 456 7890" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button onClick={() => setShowAddOwnerModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={handleAddOwner} disabled={processing === "add-owner"} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark flex items-center gap-2">
                  {processing === "add-owner" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create Owner
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Add Contributor Modal */}
        {showAddContributorModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddContributorModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-md"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" /> Add New Contributor
                </h2>
                <button onClick={() => setShowAddContributorModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={newContributorForm.email} onChange={(e) => setNewContributorForm({...newContributorForm, email: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="contributor@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username (for leaderboard) *</label>
                  <input type="text" value={newContributorForm.username} onChange={(e) => setNewContributorForm({...newContributorForm, username: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="JohnDoe123" />
                  <p className="text-xs text-gray-500 mt-1">This will be displayed on the public leaderboard</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input type="text" value={newContributorForm.name} onChange={(e) => setNewContributorForm({...newContributorForm, name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200" placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Avatar</label>
                  <select value={newContributorForm.avatar} onChange={(e) => setNewContributorForm({...newContributorForm, avatar: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200">
                    <option value="">Select an avatar...</option>
                    <option value="aragorn">🧙 Aragorn (Ranger)</option>
                    <option value="gandalf">🧙‍♂️ Gandalf (Wizard)</option>
                    <option value="legolas">🏹 Legolas (Elf)</option>
                    <option value="gimli">⚔️ Gimli (Dwarf)</option>
                    <option value="frodo">🥾 Frodo (Hobbit)</option>
                    <option value="samwise">🌱 Samwise (Gardener)</option>
                    <option value="galadriel">✨ Galadriel (Elf Queen)</option>
                    <option value="boromir">🛡️ Boromir (Warrior)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Starting XP</label>
                    <input type="number" value={newContributorForm.xp} onChange={(e) => setNewContributorForm({...newContributorForm, xp: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200" min="0" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                    <select value={newContributorForm.level} onChange={(e) => setNewContributorForm({...newContributorForm, level: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200">
                      <option value="Newcomer">Newcomer</option>
                      <option value="Explorer">Explorer</option>
                      <option value="Regular">Regular</option>
                      <option value="Expert">Expert</option>
                      <option value="Master">Master</option>
                      <option value="Champion">Champion</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button onClick={() => setShowAddContributorModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={handleAddContributor} disabled={processing === "add-contributor"} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                  {processing === "add-contributor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create Contributor
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Delete Contributor Confirmation Modal */}
        {deleteContributorModal.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setDeleteContributorModal({ show: false, contributor: null })}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Delete Contributor
                </h3>
                <button onClick={() => setDeleteContributorModal({ show: false, contributor: null })} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="p-4 bg-red-50 rounded-xl mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Warning:</strong> This will permanently delete the contributor account, their points history, and anonymize their reviews.
                  </p>
                </div>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete <strong>{deleteContributorModal.contributor?.name}</strong> ({deleteContributorModal.contributor?.email})?
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button 
                  onClick={() => setDeleteContributorModal({ show: false, contributor: null })} 
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDeleteContributor} 
                  disabled={processing === "delete-contributor"} 
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  {processing === "delete-contributor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Contributor
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Community Event Modal */}
        {showEventModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-brand" />
                  {editingEvent ? "Edit Event" : "Create Event"}
                </h3>
                <button onClick={() => setShowEventModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Title *</label>
                  <input
                    type="text"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200"
                    placeholder="Remote Workers Meetup"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                    <input
                      type="text"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                      placeholder="The Hoxton, Shoreditch"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      value={eventForm.city}
                      onChange={(e) => setEventForm({...eventForm, city: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                      placeholder="London"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <input
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      value={eventForm.time}
                      onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recurring</label>
                    <select
                      value={eventForm.recurring}
                      onChange={(e) => setEventForm({...eventForm, recurring: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                    >
                      <option value="">One-time</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200"
                    rows={3}
                    placeholder="Join us for networking and coworking..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Attendees</label>
                    <input
                      type="number"
                      value={eventForm.max_attendees || ""}
                      onChange={(e) => setEventForm({...eventForm, max_attendees: e.target.value ? parseInt(e.target.value) : null})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                      placeholder="No limit"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                    <input
                      type="url"
                      value={eventForm.image_url}
                      onChange={(e) => setEventForm({...eventForm, image_url: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="event-active"
                    checked={eventForm.is_active}
                    onChange={(e) => setEventForm({...eventForm, is_active: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
                  />
                  <label htmlFor="event-active" className="text-sm text-gray-700">Active (visible to community)</label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button onClick={() => setShowEventModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button
                  onClick={saveEvent}
                  disabled={!eventForm.title || !eventForm.location || !eventForm.city || !eventForm.date}
                  className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark flex items-center gap-2 disabled:opacity-50"
                >
                  <Calendar className="w-4 h-4" />
                  {editingEvent ? "Update Event" : "Create Event"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
